/**
 * Hooks para gestión de personas y miembros
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import { toast } from "react-hot-toast";
import {
  personService,
  subscribeToPerson,
  subscribeToPersonsByAssociation,
  subscribeToActivePersons,
} from "@/lib/services/persons";
import {
  memberService,
  subscribeToMembersByClub,
  subscribeToMembersByAssociation,
  subscribeToAllMembers,
  subscribeToMembersByPerson,
  subscribeToMembersByUnit,
  subscribeToMember,
  subscribeToMembersByLinkedUser,
} from "@/lib/services/members";
import { useAuthStore } from "@/stores/authStore";
import { usePermissions } from "@/hooks/usePermissions";
import { useRealtimeList, useRealtimeDoc } from "@/hooks/useRealtime";
import type { Person, Member, MemberStatus, MemberWithPerson } from "@/types";

// ==================== QUERY KEYS ====================

export const personKeys = {
  all: ["persons"] as const,
  scoped: (clubIds: string[]) => ["persons", "scoped", ...clubIds] as const,
  search: (term: string) => ["persons", "search", term] as const,
  byLinkedUser: (userId: string) => ["persons", "linkedUser", userId] as const,
  byDocument: (type: string, number: string) =>
    ["persons", "document", type, number] as const,
  detail: (id: string) => ["persons", id] as const,
};

export const memberKeys = {
  all: ["members"] as const,
  scoped: (clubIds: string[]) => ["members", "scoped", ...clubIds] as const,
  byClub: (clubId: string) => ["members", "club", clubId] as const,
  byClubPaginated: (clubId: string) =>
    ["members", "club", clubId, "paginated"] as const,
  byPerson: (personId: string) => ["members", "person", personId] as const,
  byUnit: (unitId: string) => ["members", "unit", unitId] as const,
  detail: (id: string) => ["members", id] as const,
  statusHistory: (memberId: string) =>
    ["members", memberId, "statusHistory"] as const,
};

// ==================== PERSON HOOKS ====================

/**
 * Hook principal para obtener personas - FILTRA POR SCOPE
 * - Director de club: solo personas de su asociación
 * - Admins: todas las personas
 */
export function usePersons() {
  const { profile } = useAuthStore();
  const { isClubDirector, isAssociationAdmin } = usePermissions();

  const associationId =
    (isClubDirector && profile?.directorOfClub?.associationId) ||
    (isAssociationAdmin && profile?.associationId) ||
    undefined;

  return useRealtimeList<Person>(
    associationId
      ? (cb, onErr) => subscribeToPersonsByAssociation(associationId, cb, onErr)
      : (cb, onErr) => subscribeToActivePersons(cb, onErr),
    [associationId],
  );
}

export function usePerson(id: string | undefined) {
  return useRealtimeDoc<Person>(id ? (cb) => subscribeToPerson(id, cb) : null, [
    id,
  ]);
}

export function usePersonByLinkedUser(
  userId: string | undefined,
  email?: string,
) {
  return useQuery({
    queryKey: userId
      ? [...personKeys.byLinkedUser(userId), email || ""]
      : ["persons", "linkedUser", "none"],
    queryFn: () => personService.getByAuthUserId(userId!, email),
    enabled: !!userId,
    staleTime: 30 * 1000,
  });
}

export function useSearchPersons(searchTerm: string, associationId?: string) {
  return useQuery({
    queryKey: personKeys.search(searchTerm),
    queryFn: () => personService.search(searchTerm, associationId),
    enabled: searchTerm.length >= 2,
    staleTime: 30 * 1000,
  });
}

export function useFindPersonByDocument(
  documentType: Person["idDocumentType"],
  documentNumber: string,
) {
  return useQuery({
    queryKey: personKeys.byDocument(documentType, documentNumber),
    queryFn: () => personService.getByDocument(documentType, documentNumber),
    enabled: !!documentType && !!documentNumber && documentNumber.length >= 5,
    staleTime: 30 * 1000,
  });
}

export function useCreatePerson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      userId,
    }: {
      data: Omit<Person, "id" | "createdAt" | "updatedAt">;
      userId: string;
    }) => personService.create(data, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personKeys.all });
      toast.success("Persona creada exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al crear persona: ${error.message}`);
    },
  });
}

export function useUpdatePerson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Person> }) =>
      personService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: personKeys.all });
      queryClient.invalidateQueries({ queryKey: personKeys.detail(id) });
      toast.success("Persona actualizada exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al actualizar persona: ${error.message}`);
    },
  });
}

export function useDeletePerson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => personService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personKeys.all });
      toast.success("Persona eliminada exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al eliminar persona: ${error.message}`);
    },
  });
}

// ==================== MEMBER HOOKS ====================

/**
 * Hook para obtener miembros con scope automático
 * - Si se pasa clubId, usa ese club específico
 * - Si no, usa el club activo del usuario
 */
export function useMembersByClub(clubId: string | undefined) {
  const result = useRealtimeList<MemberWithPerson>(
    clubId ? (cb, onErr) => subscribeToMembersByClub(clubId, cb, onErr) : null,
    [clubId],
  );

  // Todos los directores del club ven todos los miembros — el filtro por
  // sección lo hace el usuario a mano usando los badges de la UI.
  return result;
}

/**
 * Hook para obtener todos los miembros del scope del usuario
 * - Director de club: miembros de su club
 * - Admin: todos los miembros o de un club específico
 * @deprecated Usar hooks específicos por persona y membresía
 */
export function useScopedMembers() {
  const { profile } = useAuthStore();
  const { isClubDirector, isAssociationAdmin, isUnionAdmin } = usePermissions();

  const clubId =
    isClubDirector && profile?.directorOfClub
      ? profile.directorOfClub.id
      : null;
  const associationId =
    isAssociationAdmin && profile?.scopeId ? profile.scopeId : null;

  const result = useRealtimeList<MemberWithPerson>(
    clubId
      ? (cb, onErr) => subscribeToMembersByClub(clubId, cb, onErr)
      : associationId
        ? (cb, onErr) =>
            subscribeToMembersByAssociation(associationId, cb, onErr)
        : isUnionAdmin
          ? (cb, onErr) => subscribeToAllMembers(cb, onErr)
          : null,
    [clubId, associationId, isUnionAdmin],
  );

  // Todos los directores del club ven todos los miembros — el usuario filtra
  // en la UI usando los badges de sección.
  return result;
}

export function useMembersByClubPaginated(
  clubId: string | undefined,
  pageSize: number = 20,
) {
  return useInfiniteQuery({
    queryKey: memberKeys.byClubPaginated(clubId!),
    queryFn: async ({ pageParam }) => {
      return memberService.getByClubPaginated(
        clubId!,
        pageSize,
        pageParam ?? undefined,
      );
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.lastDoc : undefined,
    initialPageParam: undefined as
      | QueryDocumentSnapshot<DocumentData>
      | undefined,
    enabled: !!clubId,
  });
}

export function useMembersByPerson(personId: string | undefined) {
  return useRealtimeList<Member>(
    personId
      ? (cb, onErr) => subscribeToMembersByPerson(personId, cb, onErr)
      : null,
    [personId],
  );
}

export function useMembersByUnit(unitId: string | undefined) {
  return useRealtimeList<Member>(
    unitId ? (cb, onErr) => subscribeToMembersByUnit(unitId, cb, onErr) : null,
    [unitId],
  );
}

export function useMember(id: string | undefined) {
  return useRealtimeDoc<MemberWithPerson>(
    id ? (cb, onErr) => subscribeToMember(id, cb, onErr) : null,
    [id],
  );
}

export function useCreateMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      userId,
    }: {
      data: Omit<Member, "id" | "createdAt" | "updatedAt">;
      userId: string;
    }) => memberService.create(data, userId),
    onSuccess: (_, { data }) => {
      queryClient.invalidateQueries({
        queryKey: memberKeys.byClub(data.clubId),
      });
      if (data.personId) {
        queryClient.invalidateQueries({
          queryKey: memberKeys.byPerson(data.personId),
        });
      }
      toast.success("Miembro registrado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al registrar miembro: ${error.message}`);
    },
  });
}

export function useUpdateMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Member> }) =>
      memberService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: memberKeys.all });
      queryClient.invalidateQueries({ queryKey: memberKeys.detail(id) });
      toast.success("Miembro actualizado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al actualizar miembro: ${error.message}`);
    },
  });
}

/** F11 — Trasladar una membresía a otro club (admin). */
export function useTransferMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      memberId,
      targetClubId,
      userId,
      reason,
    }: {
      memberId: string;
      targetClubId: string;
      userId: string;
      reason?: string;
    }) => memberService.transferToClub(memberId, targetClubId, userId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.all });
      toast.success("Miembro trasladado de club");
    },
    onError: (error: Error) => {
      toast.error(`Error al trasladar: ${error.message}`);
    },
  });
}

/** F11 — Agregar un miembro a un club fusión (membresía aditiva, admin). */
export function useAddToFusion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sourceMember,
      fusionClubId,
      userId,
    }: {
      sourceMember: Member;
      fusionClubId: string;
      userId: string;
    }) => memberService.addToFusion(sourceMember, fusionClubId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.all });
    },
    onError: (error: Error) => {
      toast.error(`Error al agregar a la fusión: ${error.message}`);
    },
  });
}

export function useUpdateMemberStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: MemberStatus }) =>
      memberService.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.all });
      toast.success("Estado del miembro actualizado");
    },
    onError: (error: Error) => {
      toast.error(`Error al actualizar estado: ${error.message}`);
    },
  });
}

export function useUpdateMemberStatusWithHistory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      status,
      reason,
      userId,
    }: {
      id: string;
      status: MemberStatus;
      reason?: string;
      userId: string;
    }) => memberService.updateStatusWithHistory(id, status, reason, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: memberKeys.all });
      queryClient.invalidateQueries({
        queryKey: memberKeys.statusHistory(variables.id),
      });
      toast.success("Estado del miembro actualizado con historial");
    },
    onError: (error: Error) => {
      toast.error(`Error al actualizar estado: ${error.message}`);
    },
  });
}

export function useMemberStatusHistory(memberId: string) {
  return useQuery({
    queryKey: memberKeys.statusHistory(memberId),
    queryFn: () => memberService.getStatusHistory(memberId),
    enabled: !!memberId,
  });
}

export function useAssignMemberToUnit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, unitId }: { id: string; unitId: string }) =>
      memberService.assignToUnit(id, unitId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.all });
      toast.success("Miembro asignado a unidad");
    },
    onError: (error: Error) => {
      toast.error(`Error al asignar a unidad: ${error.message}`);
    },
  });
}

export function useDeleteMember() {
  const queryClient = useQueryClient();

  return useMutation({
    // Usa delete() del servicio que hace soft-delete + decrementa activeCount del club
    mutationFn: (id: string) => memberService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.all });
      queryClient.invalidateQueries({ queryKey: ["clubs"] });
      toast.success("Miembro desactivado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al desactivar miembro: ${error.message}`);
    },
  });
}

/**
 * BUG-002: Eliminación física (irreversible) de un miembro.
 * Borra el doc del miembro, sus ClassProgress, Achievements y EventRegistrations
 * dentro del mismo club, y decrementa contadores. Datos en otros clubes se preservan.
 */
export function useHardDeleteMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => memberService.hardDelete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.all });
      queryClient.invalidateQueries({ queryKey: ["clubs"] });
      queryClient.invalidateQueries({ queryKey: ["classProgress"] });
      queryClient.invalidateQueries({ queryKey: ["achievements"] });
      toast.success("Miembro eliminado permanentemente");
    },
    onError: (error: Error) => {
      toast.error(`Error al eliminar miembro: ${error.message}`);
    },
  });
}

export function useReactivateMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      memberService.updateStatusWithHistory(
        id,
        "active",
        "Reactivado por director",
        userId,
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: memberKeys.all });
      queryClient.invalidateQueries({ queryKey: ["clubs"] });
      queryClient.invalidateQueries({
        queryKey: memberKeys.statusHistory(variables.id),
      });
      toast.success("Miembro reactivado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(`Error al reactivar miembro: ${error.message}`);
    },
  });
}

export function useCountMembersByClub(clubId: string | undefined) {
  return useQuery({
    queryKey: [...memberKeys.byClub(clubId!), "count"],
    queryFn: () => memberService.countByClub(clubId!),
    enabled: !!clubId,
  });
}

// ==================== USER MEMBERSHIP HOOKS ====================

/**
 * Hook para obtener las membresías de un usuario (por linkedUserId)
 * Útil para mostrar "Mis Clubes" al usuario logueado
 */
export function useMembersByLinkedUser(userId: string | undefined) {
  return useRealtimeList<Member>(
    userId
      ? (cb, onErr) => subscribeToMembersByLinkedUser(userId, cb, onErr)
      : null,
    [userId],
  );
}

/**
 * Hook para vincular una persona existente con un usuario
 * Se usa cuando un usuario verifica su email y necesita vincular su cuenta con su Person
 */
export function useLinkPersonToUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      email,
      userId,
    }: {
      email: string;
      userId: string;
    }) => {
      // Buscar persona por email
      const person = await personService.getByEmail(email);
      if (!person) {
        return 0; // No se encontró persona con ese email
      }

      // Vincular persona con usuario
      await personService.linkToUser(person.id, userId);
      return 1;
    },
    onSuccess: (linkedCount) => {
      queryClient.invalidateQueries({ queryKey: personKeys.all });
      queryClient.invalidateQueries({ queryKey: memberKeys.all });
      if (linkedCount > 0) {
        toast.success("Tu cuenta ha sido vinculada a tu perfil de miembro");
      }
    },
    onError: (error: Error) => {
      toast.error(`Error al vincular cuenta: ${error.message}`);
    },
  });
}

/**
 * Hook para cambiar el cargo de un miembro dentro de su club
 */
export function useChangeMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      memberId,
      newRole,
      period,
      changedBy,
    }: {
      memberId: string;
      newRole: import("@/types").MemberRole;
      period: string;
      changedBy: string;
    }) => memberService.changeRole(memberId, newRole, period, changedBy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberKeys.all });
      toast.success("Cargo del miembro actualizado");
    },
    onError: (error: Error) => {
      toast.error(`Error al cambiar cargo: ${error.message}`);
    },
  });
}
